import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import EmptyState from '@/components/ui/EmptyState';
import Lightbox from '@/components/ui/Lightbox';
import SectionHeader from '@/components/ui/SectionHeader';
import CandidatePicker from '@/components/production/CandidatePicker';
import FixCharacterButton from '@/components/production/tiles/FixCharacterButton';
import InstructButton from '@/components/production/tiles/InstructButton';
import IteratingChip from '@/components/production/tiles/IteratingChip';
import RejectedSection from '@/components/production/tiles/RejectedSection';
import ReworkPendingOverlay from '@/components/production/tiles/ReworkPendingOverlay';
import RollBadge from '@/components/production/tiles/RollBadge';
import ShotReferencesStrip from '@/components/production/tiles/ShotReferencesStrip';
import { useProductionContext, useTileAspect } from '@/context/ProductionContext';
import { useShotBoard } from '@/hooks/useShotBoard';
import { bustSrc, normKey, thumbSrc } from '@/lib/assetKeys';
import { cn } from '@/lib/utils';

/** The storyboard: one still frame per planned shot, before anything is animated. */
export default function KeyframeGallery() {
  const { t } = useTranslation(['production', 'common']);
  const { assets, shotPlan, productionId, pendingRework, finalAssets, running, reworkBust, vertical } = useProductionContext();
  const tileAspect = useTileAspect();
  const { items, rejected, metaById, byShot, displayIds, failedSrcs, markFailed, all } = useShotBoard(
    assets,
    shotPlan,
    'keyframe',
  );
  const [enlarged, setEnlarged] = useState<string | null>(null);
  // Promoting a different take rewrites keyframes/{id}.png in place, so the URL must
  // change for the promoted frame to show. Combined with any rework bust, so a frame
  // that was both reworked and swapped still refetches.
  const [swapBust, setSwapBust] = useState<Record<string, number>>({});
  const bumpSwap = useCallback((name: string) => {
    setSwapBust((s) => ({ ...s, [normKey(name)]: (s[normKey(name)] || 0) + 1 }));
  }, []);
  const kfBust = (id: string) => (reworkBust[normKey(id)] || 0) + (swapBust[normKey(id)] || 0) || undefined;

  if (displayIds.length === 0) {
    return (
      <EmptyState title={t('storyboard.emptyTitle')}>
        {t('storyboard.empty')} {t('storyboard.help')}
      </EmptyState>
    );
  }

  const step = (dir: -1 | 1) => {
    const idx = items.findIndex((k) => k.shot_id === enlarged);
    const next = idx + dir;
    if (idx >= 0 && next >= 0 && next < items.length) setEnlarged(items[next].shot_id);
  };
  const meta = enlarged ? metaById[enlarged] : undefined;

  return (
    <div>
      <SectionHeader
        title={t('step.storyboard')}
        help={t('storyboard.help')}
        subtitle={t('storyboard.ready', { done: items.length, total: displayIds.length })}
      />

      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {displayIds.map((shotId) => {
          const kf = byShot.get(shotId);
          const m = metaById[shotId];
          const missing = !kf || failedSrcs.has(kf.path);

          if (missing) {
            return (
              <div key={shotId} className="relative overflow-hidden rounded-lg border border-dashed border-border bg-muted/30">
                <div className={cn('flex items-center justify-center', tileAspect)}>
                  <Loader2 className="h-4 w-4 animate-spin text-faint" />
                </div>
                {m && <RollBadge roll={m.roll} label={m.rollLabel} dim />}
                <div className="border-t border-border px-2 py-1.5">
                  <p className="truncate font-mono text-[9px] font-medium text-faint">
                    {shotId} · {kf ? t('state.rendering', { ns: 'common' }) : t('state.queued', { ns: 'common' })}
                    {m?.shotType ? ` · ${m.shotType.toUpperCase()}` : ''}
                  </p>
                  {m?.description && <p className="line-clamp-2 text-[10px] leading-snug text-faint">{m.description}</p>}
                </div>
              </div>
            );
          }

          const iterating = running && !finalAssets.has(`keyframe:${normKey(shotId)}`);
          return (
            <div
              key={shotId}
              className="group relative cursor-pointer overflow-hidden rounded-lg border border-border
                         bg-card transition-colors hover:border-primary/60"
              onClick={() => setEnlarged(enlarged === shotId ? null : shotId)}
            >
              <img
                src={thumbSrc(bustSrc(kf.path, kfBust(shotId)))}
                alt={shotId}
                onError={() => markFailed(kf.path)}
                // Frames still iterating (not yet QC-accepted) render B&W and
                // colourise once the identity/location gates accept them.
                className={cn('w-full object-cover transition-[filter] duration-700', tileAspect, iterating && 'grayscale')}
              />
              <div className="border-t border-border px-2 py-1.5">
                <p className="truncate font-mono text-[9px] font-medium text-muted-foreground">
                  {shotId}
                  {m?.shotType ? ` · ${m.shotType.toUpperCase()}` : ''}
                </p>
                {m?.description && <p className="line-clamp-2 text-[10px] leading-snug text-muted-foreground">{m.description}</p>}
              </div>
              {m && <RollBadge roll={m.roll} label={m.rollLabel} />}
              {/* Change requests are hidden while the frame is still iterating (not yet
                  QC-accepted) or already has a queued rework — aiming them at an
                  unsettled asset only causes confusion. */}
              {!iterating && !pendingRework.has(normKey(shotId)) && (
                <div className="absolute left-1 top-1 z-20 flex flex-col items-start gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <InstructButton productionId={productionId} assetRef={`keyframe:${shotId}`} label={shotId} />
                  <FixCharacterButton productionId={productionId} shotId={shotId} />
                  <CandidatePicker
                    productionId={productionId}
                    assetType="keyframe"
                    assetName={shotId}
                    onSwapped={bumpSwap}
                    vertical={vertical}
                  />
                </div>
              )}
              {pendingRework.has(normKey(shotId)) && <ReworkPendingOverlay />}
              {iterating && <IteratingChip />}
            </div>
          );
        })}
      </div>

      <RejectedSection title={t('storyboard.rejected')} count={rejected.length} note={t('storyboard.rejectedNote')}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-1.5">
          {rejected.map((kf) => (
            <div key={kf.shot_id} className="group relative cursor-pointer" onClick={() => setEnlarged(kf.shot_id)}>
              <img
                src={thumbSrc(kf.path)}
                alt={kf.shot_id}
                className={cn('w-full rounded border border-border object-cover grayscale group-hover:grayscale-0', tileAspect)}
              />
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-1 rounded-b
                           bg-black/70 px-1 py-0.5 font-mono text-[8px] text-white"
              >
                <span className="truncate">{kf.shot_id.replace('rejected/', '')}</span>
                <a href={kf.path} download onClick={(e) => e.stopPropagation()} className="shrink-0 text-primary hover:underline">
                  {t('action.download', { ns: 'common' })}
                </a>
              </div>
            </div>
          ))}
        </div>
      </RejectedSection>

      {enlarged && (
        <Lightbox
          label={enlarged}
          onClose={() => setEnlarged(null)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          footer={<ShotReferencesStrip shotId={enlarged} productionId={productionId} shotPlan={shotPlan} assets={assets} />}
        >
          <img
            src={bustSrc((byShot.get(enlarged) || all.find((k) => k.shot_id === enlarged))?.path || '', kfBust(enlarged))}
            alt={enlarged}
            className="mx-auto max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
          />
          {meta && (
            <div className="mt-2 rounded-lg bg-black/70 p-3 text-white">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-white/80">
                {meta.scene > 0 && <span>Scene {meta.scene}</span>}
                {meta.shotType && <span>· {meta.shotType}</span>}
                <span>· {meta.rollLabel}</span>
                {meta.location && <span className="truncate font-normal normal-case text-white/60">· {meta.location}</span>}
              </div>
              {meta.description && <p className="mt-1 text-sm leading-snug">{meta.description}</p>}
              {meta.camera && (
                <p className="mt-1 text-xs text-white/60">
                  <span className="font-semibold uppercase">Camera:</span> {meta.camera}
                </p>
              )}
            </div>
          )}
        </Lightbox>
      )}
    </div>
  );
}
